import { create } from "zustand";
import { persist } from "zustand/middleware";

import type { OfficeIdentity } from "@/api/schemas";
import type { SprancaDict } from "@/api/client";

/** Built-in šprance the user can pick in Postavke. ``null`` = Permitko
 * standard (today's document look). Ids match the backend registry
 * (``permitpilot/docgen/spranca/profiles.py``); ``"custom"`` appears only
 * when an uploaded office špranca exists (D1). */
export const SPRANCA_OPTIONS: { id: string | null; label: string }[] = [
  { id: null, label: "Permitko standard" },
  { id: "ured_generic", label: "Špranca ureda" },
  { id: "komora", label: "Špranca komore" },
];

/** Options including the uploaded office špranca when one exists. */
export function sprancaOptions(
  custom: SprancaDict | null,
): { id: string | null; label: string }[] {
  if (!custom) return SPRANCA_OPTIONS;
  return [
    ...SPRANCA_OPTIONS,
    { id: "custom", label: `${custom.name} (učitana)` },
  ];
}

interface OfficeStore {
  /** The architect's office letterhead identity, or null until set. */
  office: OfficeIdentity | null;
  /** S2 — active špranca id (null = Permitko standard). Sent with export
   * and Mapa 1 requests; the backend resolves unknown ids to null, so an
   * outdated persisted id can never break an export. */
  sprancaId: string | null;
  /** D1 — the uploaded & confirmed office špranca (localStorage mirror of
   * the server's ``data/sprance/custom.json``; offline-first like the
   * office profile). Requests send this full dict when
   * ``sprancaId === "custom"`` so exports work even if the server copy is
   * gone. */
  customSpranca: SprancaDict | null;
  setOffice: (office: OfficeIdentity | null) => void;
  setSprancaId: (id: string | null) => void;
  setCustomSpranca: (spranca: SprancaDict | null) => void;
}

/**
 * P3 — global office identity. One office per user/browser (an architect works
 * under one office), persisted to localStorage and reused across every project.
 * It carries only the letterhead (name / tagline / contact); the legal wording
 * of the izjave stays the standard base profile on the backend.
 */
export const useOfficeStore = create<OfficeStore>()(
  persist(
    (set) => ({
      office: null,
      sprancaId: null,
      customSpranca: null,
      setOffice: (office) => set({ office }),
      setSprancaId: (sprancaId) => set({ sprancaId }),
      setCustomSpranca: (customSpranca) =>
        set((s) => ({
          customSpranca,
          // Removing the uploaded špranca while it is active falls back to
          // the Permitko standard — a dangling "custom" id would resolve
          // to null on the backend anyway; keep the UI honest about it.
          sprancaId:
            customSpranca === null && s.sprancaId === "custom"
              ? null
              : s.sprancaId,
        })),
    }),
    { name: "permitpilot.office.v1" },
  ),
);

/** The payload sent to the backend — null when nothing meaningful is set. */
export function officePayload(office: OfficeIdentity | null): OfficeIdentity | null {
  if (!office) return null;
  const has = Object.values(office).some(
    (v) => typeof v === "string" && v.trim() !== "",
  );
  return has ? office : null;
}

/**
 * D1 — the ``spranca`` request value every generate/export/mapa call sends:
 * the FULL dict for the uploaded špranca (survives a lost server copy),
 * the id for built-ins, null for the Permitko standard.
 */
export function sprancaRequestValue(): string | SprancaDict | null {
  const { sprancaId, customSpranca } = useOfficeStore.getState();
  if (sprancaId === "custom") return customSpranca ?? null;
  return sprancaId;
}
